import type { QueryKey } from "@tanstack/react-query";
import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { OptionCategoryHistoryEntry } from "../optionCategoryList/types";
import type { OptionCategoryFormValues, OptionCategoryType } from "./optionCategories";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";

const baseEndpoint = (categoryType: OptionCategoryType) =>
  `${FORMS_API_BASE_ENDPOINT}/enterprise-customer/option-categories/${categoryType}`;

const keys = {
  all: (categoryType: OptionCategoryType): QueryKey => ["enterpriseCustomerOptionCategories", categoryType],
  list: (categoryType: OptionCategoryType): QueryKey => [...keys.all(categoryType), "list"],
  history: (categoryType: OptionCategoryType): QueryKey => [...keys.all(categoryType), "history"],
  selectOptions: (categoryType: OptionCategoryType): QueryKey => [...keys.all(categoryType), "selectOptions"],
} as const;

export const sharedOptionCategoriesQueryOptions = (categoryType: OptionCategoryType) =>
  queryOptions({
    queryKey: keys.list(categoryType),
    queryFn: () =>
      api.get(baseEndpoint(categoryType), {
        schema: z.object({
          options: z.array(
            z.object({
              id: z.string(),
              value: z.string(),
              label: z.string(),
              canBeEdited: z.boolean(),
              isInUse: z.boolean(),
              metadata: z.object({ description: z.string().optional() }).optional(),
              connectedEntities: z.array(z.object({ formType: z.string(), numberOfConnections: z.number() })),
            })
          ),
        }),
      }),
    throwOnError: true,
  });

export const sharedOptionCategoriesHistoryQueryOptions = (categoryType: OptionCategoryType) =>
  queryOptions({
    queryKey: keys.history(categoryType),
    queryFn: () =>
      api.get(`${baseEndpoint(categoryType)}/history`, {
        schema: z.custom<{ history: Array<OptionCategoryHistoryEntry> }>(),
      }),
    throwOnError: true,
  });

export const sharedOptionCategorySelectOptionsQueryOptions = (categoryType: OptionCategoryType) =>
  queryOptions({
    queryKey: keys.selectOptions(categoryType),
    queryFn: () =>
      api.get(`${baseEndpoint(categoryType)}/select-options`, {
        schema: z.object({
          options: z.array(
            z.object({
              label: z.string(),
              value: z.string(),
              metadata: z.object({ description: z.string().optional() }).optional(),
            })
          ),
        }),
      }),
    throwOnError: true,
  });

type SharedOptionCategoryVariables = { categoryType: OptionCategoryType; values: OptionCategoryFormValues };

const toSharedOptionPayload = ({ categoryType, values: { label, description } }: SharedOptionCategoryVariables) => ({
  label,
  metadata: { type: `${categoryType}Metadata`, description },
});

export const createSharedOptionCategoryMutationOptions = () =>
  mutationOptions({
    mutationFn: (variables: SharedOptionCategoryVariables) =>
      api.post(baseEndpoint(variables.categoryType), toSharedOptionPayload(variables), {
        schema: z.object({ id: z.string(), value: z.string() }),
      }),
    onSuccess: () => invalidateAll(),
  });

export const updateSharedOptionCategoryMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, ...variables }: SharedOptionCategoryVariables & { id: string }) =>
      api.patch(`${baseEndpoint(variables.categoryType)}/${id}`, toSharedOptionPayload(variables)),
    onSuccess: () => invalidateAll(),
  });

export const deleteSharedOptionCategoryMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ categoryType, id }: { categoryType: OptionCategoryType; id: string }) =>
      api.delete(`${baseEndpoint(categoryType)}/${id}`),
    onSettled: () => invalidateAll(),
  });
